import { groupBy } from "lodash";
import { ReactElement } from "react";
import ReactMarkdown from "react-markdown";

import { CopilotAction, CopilotMessage } from "../../api/types.gen.ts";
import { nodeLinkRegex } from "../../utils/regex-definitions.ts";
import { CopyButton } from "../CopyButton.tsx";
import {
  markdownComponents,
  preprocessCopilotMarkdown,
} from "./markdown-components.tsx";
import { RunQueryActionGroup } from "./RunQueryActionGroup.tsx";

export interface AIAssistantMessageProps {
  message: CopilotMessage;
}

// Preprocess markdown: custom copilot tags, then node:// links
const preprocessMarkdown = (content: string): string => {
  const withCustomTags = preprocessCopilotMarkdown(content);
  // Convert [text](node://id) to [text](#node-id) format that ReactMarkdown can handle
  return withCustomTags.replace(nodeLinkRegex, "[$1](#node-$2)");
};

export const AIAssistantMessage = ({
  message,
}: AIAssistantMessageProps): ReactElement => {
  const processedContent = preprocessMarkdown(message.content);

  const renderActions = (actions: CopilotAction[]): ReactElement => {
    // `CopilotAction` no longer includes an explicit `type` discriminator.
    // All current actions are "run query action" style, grouped by queryAction ID.
    const runQueryActionGroups = groupBy(
      actions,
      (action) => action.params.queryAction?.id ?? "_missing",
    );

    return (
      <div className="mt-4 space-y-4">
        {Object.keys(runQueryActionGroups).length > 0 && (
          <div className="space-y-2">
            {Object.entries(runQueryActionGroups).map(
              ([queryActionId, actions]) => (
                <RunQueryActionGroup
                  actions={actions}
                  groupName={
                    actions[0]?.description?.split(" relationship on ")[0] ||
                    "Query Action"
                  }
                  key={queryActionId}
                />
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  const renderedActions = message.actions
    ? renderActions(message.actions)
    : null;

  return (
    <div className="space-y-3">
      <div className="relative">
        <ReactMarkdown components={markdownComponents}>
          {processedContent}
        </ReactMarkdown>
        {renderedActions}
        {message.isPartial && (
          <div className="mt-2 text-xs italic text-gray-500">
            Response stopped by user
          </div>
        )}
        <div className="mt-2 flex justify-start">
          <CopyButton
            content={message.content}
            errorMessage="Failed to copy response to clipboard"
            successMessage="Response copied to clipboard"
            title="Copy response"
          />
        </div>
      </div>
    </div>
  );
};
